import { createdATFilter } from 'modules/common/constants/filters/common.filters';
import { Filter, FilterType } from '@dfl/mui-admin-layout';
import { PRODUCT_LIST_KEY } from './query-keys';
import { CategoryService } from 'modules/inventory/settings/category/services';
import { CATEGORIES_LIST_KEY } from 'modules/inventory/settings/category/constants';
import { LogisticsService } from 'modules/inventory/provider/logistics/services';
import { SupplierService } from 'modules/inventory/provider/supplier/services';
import { EmptyFilter, OperatorFilter, TermFilter } from '@dofleini/query-builder';
import { SUPPLIER_LIST_KEY } from 'modules/inventory/provider/supplier/constants';
import { LOGISTICS_LIST_KEY } from 'modules/inventory/provider/logistics/constants';
import { WarehouseService } from 'modules/inventory/warehouse/services';
import { STATUS } from 'modules/inventory/provider/common/constants/status.filter';
import { WAREHOUSES_LIST_KEY } from 'modules/inventory/warehouse/constants';
import { transformWhitObjectId } from 'modules/common/constants/object-id';
import { availableFilter, reservationFilter, stockFilter } from './product-stock.filters';
import { sanitizeTextFilter } from 'utils/search.utils';
import { LabelsService } from 'modules/inventory/settings/labels/services';
import { LABELS_LIST_KEY } from 'modules/inventory/settings/labels/constants';

export const getLogisticProviderFilter = (field: string = 'logisticProvider'): Filter => ({
  filter: 'common:logisticProvider',
  translate: true,
  type: FilterType.DYNAMIC_LIST,
  key: field,
  labelKey: 'name',
  field,
  fetchFunc: LogisticsService.search,
  fetchOption: { size: 200 },
  queryKey: LOGISTICS_LIST_KEY,
  transform: (value) => transformWhitObjectId(value, field),
});

export const codeFilter = (field?: string): Filter => ({
  filter: 'product:fields.code',
  translate: true,
  type: FilterType.TEXT,
  key: 'code',
  field: field || 'code',
  transform: (value) => {
    const queryText = sanitizeTextFilter(value);
    if (!queryText) return undefined;
    return new TermFilter({ field: field || 'code', value: queryText });
  },
});

export const categoryFilter: Filter = {
  filter: 'product:fields.category',
  translate: true,
  type: FilterType.DYNAMIC_LIST,
  key: 'category',
  labelKey: 'name',
  field: 'category.categoryPath',
  fetchFunc: CategoryService.search,
  fetchOption: { size: 200 },
  queryKey: CATEGORIES_LIST_KEY,
  transform: (value) => transformWhitObjectId(value, 'category.categoryPath'),
};

export const brandFilter: Filter = {
  filter: 'product:fields.brand',
  translate: true,
  type: FilterType.TEXT,
  key: 'brand',
  field: 'brand',
  transform: (value: any) => {
    const queryText = sanitizeTextFilter(value);
    if (!queryText) return undefined;
    return new TermFilter({
      field: 'brand',
      value: {
        $regex: queryText,
        $options: 'i',
      },
    });
  },
};

export const shippingFilter: Filter = {
  filter: 'product:filterName.shippingFree.title',
  translate: true,
  type: FilterType.BOOL,
  queryKey: PRODUCT_LIST_KEY,
  key: 'free',
  labelKey: 'free',
  field: 'shippingSettings.freeShipping',
};

export const offerFilter: Filter = {
  filter: 'common:offer',
  translate: true,
  type: FilterType.DYNAMIC_LIST,
  key: 'offer',
  labelKey: 'offer',
  fetchOption: { size: 200 },
  field: 'offer',
  transform: (value) => transformWhitObjectId(value, 'offer'),
};
export const costFilter = (field?: string): Filter => ({
  filter: 'common:cost',
  translate: true,
  type: FilterType.NUMBER,
  key: 'cost',
  field: field || 'priceDetails.values.cost',
});

export const statusFilter: Filter = {
  filter: 'product:fields:visibility',
  type: FilterType.FIXED_LIST,
  translate: true,
  key: 'visible',
  field: 'visible',
  options: Object.keys(STATUS).map((key) => ({
    value: STATUS[key],
    translate: true,
    label: `product:statusProduct.${key.toLocaleLowerCase()}`,
  })),
  transform: (value) => {
    if (Array.isArray(value)) return new EmptyFilter();
    switch (value) {
      case 'false':
        return new OperatorFilter({
          type: 'OR',
          filters: [
            new TermFilter({ field: 'visible', value: false }),
            new TermFilter({ field: 'visible', value: null }),
          ],
        }).toQuery();
      case 'true':
        return new TermFilter({ field: 'visible', value: true }).toQuery();
    }
  },
};

export const totalStockFilter: Filter = {
  filter: 'product:section.inventory.available',
  translate: true,
  type: FilterType.NUMBER,
  key: 'totalStock',
  field: 'totalStock',
};

export const priceFilter = (field?: string): Filter => ({
  filter: 'common:price',
  translate: true,
  type: FilterType.NUMBER,
  key: 'price',
  field: field || 'finalPrice',
});

export const productProviderFilter: Filter = {
  filter: 'common:productProvider',
  translate: true,
  type: FilterType.DYNAMIC_LIST,
  key: 'productProvider',
  labelKey: 'name',
  field: 'providers.supplier._id',
  fetchFunc: SupplierService.search,
  fetchOption: { size: 200 },
  queryKey: SUPPLIER_LIST_KEY,
  transform: (value) => transformWhitObjectId(value, 'providers.supplier._id'),
};

export const logisticProviderFilter: Filter = getLogisticProviderFilter();

export const stockStoreFilter: Filter = {
  filter: 'common:warehouse',
  translate: true,
  type: FilterType.DYNAMIC_LIST,
  key: 'warehouse',
  labelKey: 'name',
  field: 'stock.warehouse',
  fetchFunc: WarehouseService.search,
  fetchOption: { size: 200 },
  queryKey: WAREHOUSES_LIST_KEY,
  transform: (value) => transformWhitObjectId(value, 'stock.warehouse'),
};

export const offerEnabledFilter: Filter = {
  filter: 'product:filterName.offer.title',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'offer',
  field: 'offer.enabled',
  transform: (value: any) => {
    if (Array.isArray(value)) return new EmptyFilter();
    switch (value) {
      case 'false':
        return new OperatorFilter({
          type: 'OR',
          filters: [
            new TermFilter({ field: 'offer.enabled', value: false }),
            new TermFilter({ field: 'offer.enabled', value: null }),
          ],
        }).toQuery();
      case 'true':
        return new TermFilter({ field: 'offer.enabled', value: true }).toQuery();
    }
  },
  options: [
    {
      value: 'true',
      translate: true,
      label: 'product:filterName.offer.free',
    },
    {
      value: 'false',
      translate: true,
      label: 'product:filterName.offer.noFree',
    },
  ],
};

export const productOfferFilter: Filter = {
  filter: 'product:filterName.offer.title',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'offer',
  field: 'scheduledOffers',
  transform: (value) => {
    if (Array.isArray(value)) return new EmptyFilter();

    return value === 'false'
      ? new EmptyFilter()
      : new TermFilter({ field: 'priceDetails.values.offer', value: { $ne: 0 } }).toQuery();
  },
  options: [
    {
      value: 'true',
      translate: true,
      label: 'product:filterName.offer.free',
    },
    {
      value: 'false',
      translate: true,
      label: 'product:filterName.offer.noFree',
    },
  ],
};

/* limitByAge;

limitByOrder;

needCi;

onlyOnDemand; */

export const productRulesFilter: Filter = {
  filter: 'product:section.shippingInfo.rules',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'rules',
  field: 'rules',
  transform: (value) => {
    const rules = Array.isArray(value) ? value : [value];
    const filters = [];

    if (!rules.length || rules.length === 0) return new EmptyFilter();

    if (rules.includes('LIMIT_BY_AGE')) {
      filters.push(new TermFilter({ field: 'rules.limitByAge', value: true }));
    }
    if (rules.includes('LIMIT_BY_ORDER')) {
      filters.push(new TermFilter({ field: 'rules.limitByOrder', value: { $gt: 0 } }));
    }

    if (rules.includes('MIN_BY_ORDER')) {
      filters.push(new TermFilter({ field: 'rules.minByOrder', value: { $gt: 0 } }));
    }

    if (rules.includes('NEED_CI')) {
      filters.push(new TermFilter({ field: 'rules.needCi', value: true }));
    }
    if (rules.includes('ONLY_ON_DEMAND')) {
      filters.push(new TermFilter({ field: 'rules.onlyOnDemand', value: true }));
    }
    if (rules.includes('FREE_SHIPPING')) {
      filters.push(new TermFilter({ field: 'rules.freeShipping', value: true }));
    }
    if (rules.includes('ONLY_BUSINESS')) {
      filters.push(new TermFilter({ field: 'rules.onlyBusiness', value: true }));
    }
    return new OperatorFilter({
      type: 'OR',
      filters,
    }).toQuery();
  },
  options: [
    {
      value: 'LIMIT_BY_AGE',
      translate: true,
      label: 'provider:rules.olderAge',
    },
    {
      value: 'LIMIT_BY_ORDER',
      translate: true,
      label: 'provider:rules.limitByDelivery',
    },
    {
      value: 'MIN_BY_ORDER',
      translate: true,
      label: 'provider:rules.minByDelivery',
    },
    {
      value: 'NEED_CI',
      translate: true,
      label: 'provider:rules.needCi',
    },
    {
      value: 'FREE_SHIPPING',
      translate: true,
      label: 'provider:rules.free',
    },
    {
      value: 'ONLY_ON_DEMAND',
      translate: true,
      label: 'product:section.shipping.onlyOnDemand',
    },
    {
      value: 'ONLY_BUSINESS',
      translate: true,
      label: 'product:section.shipping.onlyBusiness',
    },
  ],
};

export const productShippingFilter: Filter = {
  filter: 'product:filterName.shippingFree.title',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'shipping.free',
  field: 'rules.freeShipping',
  transform: (value) => {
    if (Array.isArray(value)) return new EmptyFilter();
    switch (value) {
      case 'false':
        return new OperatorFilter({
          type: 'OR',
          filters: [
            new TermFilter({ field: 'rules.freeShipping', value: false }),
            new TermFilter({ field: 'rules.freeShipping', value: null }),
          ],
        }).toQuery();
      case 'true':
        return new TermFilter({ field: 'rules.freeShipping', value: true }).toQuery();
    }
  },
  options: [
    {
      value: 'true',
      translate: true,
      label: 'product:filterName.shippingFree.free',
    },
    {
      value: 'false',
      translate: true,
      label: 'product:filterName.shippingFree.noFree',
    },
  ],
};

export const availabilityFilter: Filter = {
  filter: 'product:filterName.availability.title',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'stock',
  field: 'stock',
  transform: (value) => {
    if (Array.isArray(value)) return new EmptyFilter();
    switch (value) {
      case 'false':
        return new OperatorFilter({
          type: 'OR',
          filters: [
            new TermFilter({ field: 'stock.enable', value: false }),
            new TermFilter({ field: 'stock.enable', value: null }),
          ],
        }).toQuery();
      case 'true': {
        return new TermFilter({ field: 'stock.enable', value: true }).toQuery();
      }
    }
  },
  options: [
    {
      value: 'true',
      translate: true,
      label: 'product:filterName.availability.available',
    },
    {
      value: 'false',
      translate: true,
      label: 'product:filterName.availability.noAvailable',
    },
  ],
};

export const topFilter: Filter = {
  filter: 'product:fields:top',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'top',
  field: 'internalSeo.top',
  transform: (value) => {
    if (Array.isArray(value)) return new EmptyFilter();
    switch (value) {
      case 'false':
        return new OperatorFilter({
          type: 'OR',
          filters: [
            new TermFilter({ field: 'internalSeo.top', value: false }),
            new TermFilter({ field: 'internalSeo.top', value: null }),
          ],
        }).toQuery();
      case 'true':
        return new TermFilter({ field: 'internalSeo.top', value: true }).toQuery();
    }
  },
  options: [
    {
      value: 'true',
      translate: true,
      label: 'product:filterName.top.top',
    },
    {
      value: 'false',
      translate: true,
      label: 'product:filterName.top.noTop',
    },
  ],
};

export const warehousePickupFilter: Filter = {
  filter: 'pickupAtStore:title',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'warehousePickup',
  field: 'shippingSettings.warehousePickup.enabled',
  transform: (value) => {
    if (Array.isArray(value)) return new EmptyFilter();
    switch (value) {
      case 'false':
        return new OperatorFilter({
          type: 'OR',
          filters: [
            new TermFilter({ field: 'shippingSettings.warehousePickup.enabled', value: false }),
            new TermFilter({ field: 'shippingSettings.warehousePickup.enabled', value: null }),
          ],
        }).toQuery();
      case 'true':
        return new TermFilter({ field: 'shippingSettings.warehousePickup.enabled', value: true }).toQuery();
    }
  },
  options: [
    {
      value: 'true',
      translate: true,
      label: 'common:yes',
    },
    {
      value: 'false',
      translate: true,
      label: 'common:no',
    },
  ],
};

export const labelsFilter: Filter = {
  filter: 'product:filterName.labels.title',
  translate: true,
  type: FilterType.DYNAMIC_LIST,
  key: 'labels',
  labelKey: 'name',
  field: 'labelConfigurations.labelId',
  fetchFunc: LabelsService.search,
  queryKey: LABELS_LIST_KEY,
  transform: (value) => transformWhitObjectId(value, 'labelConfigurations.labelId'),
};

export const isVariantParentFilter: Filter = {
  filter: 'product:filterName.variants.title',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'variant',
  field: 'isVariantParent',
  transform: (value) => {
    if (Array.isArray(value)) return new EmptyFilter();
    switch (value) {
      case 'false':
        return new OperatorFilter({
          type: 'OR',
          filters: [
            new TermFilter({ field: 'isVariantParent', value: false }),
            new TermFilter({ field: 'isVariantParent', value: null }),
          ],
        }).toQuery();
      case 'true':
        return new TermFilter({ field: 'isVariantParent', value: true }).toQuery();
    }
  },
  options: [
    { value: 'true', translate: true, label: 'product:filterName.variants.withVariants' },
    { value: 'false', translate: true, label: 'product:filterName.variants.noVariants' },
  ],
};

export const productFilters = [
  codeFilter(),
  productProviderFilter,
  statusFilter,
  topFilter,
  brandFilter,
  labelsFilter,
  costFilter(),
  priceFilter(),
  totalStockFilter,
  categoryFilter,
  isVariantParentFilter,
  warehousePickupFilter,
  productShippingFilter,
  productOfferFilter,
  productRulesFilter,
  // logisticProviderFilter,
  stockStoreFilter,
  createdATFilter,
];

export const defaultProductFilters = [codeFilter(), categoryFilter, stockStoreFilter];

// /inventory/provider/suppliers/:id/inventory > warehouses/products
export const supplierStoreProductFilters = [
  codeFilter(),
  productShippingFilter,
  offerEnabledFilter,
  costFilter(),
  categoryFilter,
  statusFilter,
  availableFilter,
  reservationFilter,
  stockFilter,
  createdATFilter,
];
export const defaultSupplierStoreProductFilters = [codeFilter(), statusFilter, availabilityFilter];

// /inventory/provider/suppliers/:id/products
export const supplierProductTabFilters = [
  codeFilter(),
  costFilter(),
  brandFilter,
  categoryFilter,
  stockStoreFilter,
  productShippingFilter,
  offerEnabledFilter,
  createdATFilter,
];

export const defaultSupplierProductTabFilters = [codeFilter(), costFilter(), stockStoreFilter];

// /inventory/settings/categories/:id/products
export const subCategoryProductFilters = [
  codeFilter(),
  brandFilter,
  costFilter(),
  priceFilter(),
  productProviderFilter,
  logisticProviderFilter,
  stockStoreFilter,
  createdATFilter,
];

export const defaultSubCategoryProductFilters = [codeFilter(), costFilter(), priceFilter()];

// /inventory/settings/manufacturer/:id/products
export const manufactureProductFilters = [
  codeFilter(),
  statusFilter,
  // brandFilter,
  costFilter(),
  priceFilter(),
  categoryFilter,
  productProviderFilter,
  logisticProviderFilter,
  createdATFilter,
];

export const defaultManufactureProductFilters = [codeFilter(), costFilter(), priceFilter()];

export const productByMarginFilters = [codeFilter(), productProviderFilter, costFilter(), priceFilter()];
